import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tooltip,
} from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import RoleModalContent from "./RoleModalContent";
import {
  addRole,
  deleteRole,
  getRoles,
  updateRole,
  getPermissions,
} from "../../../utils/api";

const RolesTable = () => {
  const [data, setData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [permissions, setPermissions] = useState([]);

  const searchInput = useRef(null);
  const [searchText, setSearchText] = useState("");

  const fetchAllRoles = async () => {
    try {
      setDataLoading(true);
      const res = await getRoles();
      setData(res.data.data || []);
    } catch (err) {
      message.error("Error fetching Roles!");
    } finally {
      setDataLoading(false);
    }
  };

  const fetchAllPermissions = async () => {
    try {
      const res = await getPermissions();
      setPermissions(res.data.data || []);
    } catch (err) {
      message.error("Error fetching Permissions!");
    }
  };

  useEffect(() => {
    fetchAllRoles();
    fetchAllPermissions();
  }, []);

  const handleCreate = async (values) => {
    try {
      await addRole(values);
      await fetchAllRoles();
      setCreateModalOpen(false);
    } catch (err) {
      message.error("Error creating Role!");
    }
  };

  const handleUpdate = async (id, values) => {
    try {
      await updateRole(id, values);
      await fetchAllRoles();
      setEditModalOpen(false);
      setCurrentRole(null);
    } catch (err) {
      message.error("Error updating Role!");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteRole(id);
      await fetchAllRoles();
    } catch (err) {
      message.error("Error deleting Role!");
    }
  };

  // Function to render permissions with tooltip if too many
  const renderPermissions = (permissionsList) => {
    if (!permissionsList || permissionsList.length === 0)
      return "No Permissions";

    const MAX_VISIBLE = 3; // Number of permissions to show inline
    const allPermissions = permissionsList
      .map((p) => p.display_name)
      .join(", ");
    let displayedText = allPermissions;

    if (permissionsList.length > MAX_VISIBLE) {
      const firstFew = permissionsList
        .slice(0, MAX_VISIBLE)
        .map((p) => p.display_name)
        .join(", ");
      displayedText = `${firstFew}, ...`;
    }

    return (
      <Tooltip title={allPermissions}>
        <span>{displayedText}</span>
      </Tooltip>
    );
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Permissions",
      dataIndex: "permissions",
      key: "permissions",
      render: (value) => renderPermissions(value),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <EditOutlined
            className="cursor-pointer"
            onClick={() => {
              setCurrentRole(record);
              setEditModalOpen(true);
            }}
          />
          <Popconfirm
            title="Are you sure to delete this role?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <DeleteOutlined className="cursor-pointer text-dangerRed" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button type="primary" onClick={() => setCreateModalOpen(true)}>
          Create Role
        </Button>
      </div>
      <Table
        dataSource={data}
        columns={columns}
        loading={dataLoading}
        rowKey="id"
      />
      <Modal
        title="Create Role"
        open={createModalOpen}
        footer={null}
        onCancel={() => setCreateModalOpen(false)}
      >
        <RoleModalContent
          permissions={permissions}
          onSubmit={handleCreate}
          onCancel={() => setCreateModalOpen(false)}
        />
      </Modal>

      <Modal
        title="Edit Role"
        open={editModalOpen}
        footer={null}
        onCancel={() => {
          setEditModalOpen(false);
          setCurrentRole(null);
        }}
      >
        <RoleModalContent
          initialData={currentRole}
          permissions={permissions}
          onSubmit={(values) => handleUpdate(currentRole.id, values)}
          onCancel={() => {
            setEditModalOpen(false);
            setCurrentRole(null);
          }}
        />
      </Modal>
    </>
  );
};

export default RolesTable;
